import { formatArea, formatLength } from '@/ui/dom';
import type { WandContext, WandTarget } from './WandContext';

/**
 * Everything the wand says, and everything it offers to say for you.
 *
 * Kept as pure functions over the context rather than as strings inlined in the panel, for the same
 * reason the context itself is a record: this is the part of the feature most likely to be wrong, and
 * it is testable here and untestable there.
 *
 * ## The voice
 *
 * The tool is called the **wand**, never "AI" and never "assistant". That is not decoration. The rest
 * of the app names tools by what they do to the site — Plant, Sculpt, Paint, Enhance — and a control
 * named after its implementation would be the only one that isn't. It also sets the right
 * expectation: a wand is something you point, not something you converse with.
 *
 * ## Starters exist because a blank box teaches nothing
 *
 * A text field with a placeholder is a promise the user has no way to check. Four one-tap starters,
 * chosen by what was actually clicked, are the whole discovery mechanism for the feature: they show
 * the *shape* of a good request — a verb, a scale, a place — and each is a real sentence someone
 * would type, not a category label.
 */

/* -------------------------------- Headlines -------------------------------- */

/**
 * The bubble's subtitle: what the wand understands itself to be pointed at.
 *
 * Shown before the user types anything, because the most common failure of a tool like this is not
 * a bad answer but a request aimed somewhere the user did not mean.
 */
export function targetHeadline(
  context: WandContext,
  unit: 'meters' | 'feet' = 'meters',
): string {
  const { target } = context;
  const selection = context.selection.length;

  // The drawn area comes first: it is the only target the designer had to make an effort to state,
  // and reporting anything else back would say the effort was not registered.
  if (target.region) {
    return (
      `Drawn area · ${formatArea(target.region.areaMeters, unit)} · ` +
      `${formatLength(target.region.widthMeters, unit)} × ${formatLength(target.region.lengthMeters, unit)}`
    );
  }
  if (selection > 1) {
    return `${selection} plants selected`;
  }
  if (target.plant) {
    return `${target.plant.name} · ${Math.round(target.plant.maturity * 100)}% grown · ${formatLength(target.plant.heightMeters, unit)} tall`;
  }
  if (target.structure) {
    const s = target.structure;
    return `${s.name} · ${formatLength(s.widthMeters, unit)} × ${formatLength(s.depthMeters, unit)}`;
  }
  // With the grid on, the cell beats the bearing: it is a label painted on the ground in front of
  // the user, so it can be checked by looking rather than by estimating a distance from the centre.
  const where = target.gridCell ? `cell ${target.gridCell}` : bearing(target);
  return `Open ground · ${SURFACE_WORDS[target.surface] ?? target.surface} · ${where}`;
}

/**
 * Where the click landed, in words a person can check against what they see.
 *
 * Compass words rather than raw coordinates: "4 m east, 11 m south of centre" is verifiable by
 * looking at the site; "(4.2, 11.0)" is only verifiable against a readout the user is not reading.
 */
function bearing(target: WandTarget): string {
  const { x, z } = target.point;
  const parts: string[] = [];
  if (Math.abs(x) >= 0.5) parts.push(`${Math.abs(x).toFixed(1)} m ${x > 0 ? 'east' : 'west'}`);
  if (Math.abs(z) >= 0.5) parts.push(`${Math.abs(z).toFixed(1)} m ${z > 0 ? 'south' : 'north'}`);
  return parts.length > 0 ? `${parts.join(', ')} of centre` : 'the centre of the site';
}

const SURFACE_WORDS: Record<string, string> = {
  grass: 'lawn',
  mulch: 'mulched bed',
  water: 'water',
  blacktop: 'blacktop',
  gravel: 'gravel',
};

/** Placeholder for the ask box. Contextual, so it names the thing the request will be about. */
export function targetPlaceholder(context: WandContext): string {
  const selection = context.selection.length;
  if (context.target.region) return 'Describe what this area should become…';
  if (selection > 1) return `Ask for a change to these ${selection} plants…`;
  if (context.target.plant) return `Ask for a change to this ${context.target.plant.name.toLowerCase()}…`;
  if (context.target.structure) return `Ask for a change to the ${context.target.structure.name.toLowerCase()}…`;
  return 'Describe what should be here…';
}

/* --------------------------------- Starters --------------------------------- */

export interface WandStarter {
  /** The chip's short label. */
  label: string;
  /** The full sentence dropped into the ask box — a real request, not a category. */
  prompt: string;
}

/**
 * The full starter set, by what was clicked.
 *
 * Each list is ordered by how often the request is likely to be the reason someone reached for the
 * wand at that target, not by how impressive it is. The first chip is the common case.
 */
export const WAND_STARTERS: Record<
  'ground' | 'plant' | 'structure' | 'selection' | 'area',
  WandStarter[]
> = {
  // Every one of these is a sentence that only makes sense because a shape exists — which is the
  // point of showing them: they teach that the outline is now the subject of the request.
  area: [
    { label: 'Plant it', prompt: 'Plant this whole area as a mixed bed, in drifts rather than an even wash.' },
    { label: 'Make it a berm', prompt: 'Raise this area into a low planted berm, about half a metre at the crown.' },
    { label: 'Pave it', prompt: 'Pave this area, and leave a soft edge where it meets the lawn.' },
    { label: 'Clear it', prompt: 'Remove the plants inside this area.' },
  ],
  ground: [
    { label: 'Plant a bed', prompt: 'Plant a small mixed bed here, about 3 m across.' },
    { label: 'Raise a berm', prompt: 'Raise a low berm here, roughly 6 m across and half a metre high.' },
    { label: 'Lay a path', prompt: 'Lay a gravel path from here toward the centre of the site.' },
    { label: 'Put a shed here', prompt: 'Put a small garden shed here, facing the open ground.' },
  ],
  plant: [
    { label: 'Grow it up', prompt: 'Show this at full maturity.' },
    { label: 'Underplant it', prompt: 'Underplant this with a low shade-tolerant drift, staying inside its canopy.' },
    { label: 'Give it company', prompt: 'Add two more of these nearby, at different ages, so it reads as a group.' },
    { label: 'Take it out', prompt: 'Remove this plant.' },
  ],
  structure: [
    { label: 'Screen it', prompt: 'Screen this with a hedge along its longest side, one metre clear of the wall.' },
    { label: 'Plant the base', prompt: 'Plant a low foundation bed against this, on the side facing the open ground.' },
    { label: 'Pave up to it', prompt: 'Lay a paved apron in front of this, about 2 m deep.' },
    { label: 'Remove it', prompt: 'Remove this structure.' },
  ],
  selection: [
    { label: 'Age them', prompt: 'Grow these to full maturity.' },
    { label: 'Vary them', prompt: 'Vary the age of these so they do not all look the same.' },
    { label: 'Clip them', prompt: 'Clip these into a formal hedge shape.' },
    { label: 'Remove them', prompt: 'Remove these plants.' },
  ],
};

/** Starters for the current target. */
export function starterPrompts(context: WandContext): WandStarter[] {
  if (context.target.region) return WAND_STARTERS.area;
  if (context.selection.length > 1) return WAND_STARTERS.selection;
  if (context.target.plant) return WAND_STARTERS.plant;
  if (context.target.structure) return WAND_STARTERS.structure;
  return WAND_STARTERS.ground;
}

/**
 * Starters for a refinement.
 *
 * Different in kind from the first-pass ones: a refinement is nearly always a *dimension* of the last
 * result being wrong — too big, too many, wrong place, wrong plant — so these are adjustments rather
 * than requests, and they are the same four whatever was clicked.
 */
export const WAND_REFINEMENTS: readonly WandStarter[] = [
  { label: 'Smaller', prompt: 'Smaller — about half that.' },
  { label: 'Fewer', prompt: 'Fewer of them, more spread out.' },
  { label: 'Move it', prompt: 'Move it a couple of metres away from where it is now.' },
  { label: 'Different plant', prompt: 'Try a different species — something that suits the spot better.' },
];
